<template>
  <div v-if="taggingMessage" class="message-details-wrapper">
    <AlertErrorMsg
      ref="alert"
      :title="errorMessage"
      class="page-alert-error"
    />
    <div class="message-details-header">
      <h2 class="message-details-title">
        Details
      </h2>
      <WhiteCloseIcon28 @click="closeDetails" />
    </div>
    <div class="menu-item-wrapper">
      <div class="menu-item-header">
        <h2 class="menu-item-title">
          Message
        </h2>
        <GreyArrowUpIcon24
          :style="{ transform: isViewMessage ? '' : 'rotate(180deg)' }"
          @click="isViewMessage = !isViewMessage"
        />
      </div>
      <transition v-if="isViewMessage" name="slide">
        <div class="message-text">
          <div class="message-wrapper">
            <div class="relative">
              <span v-if="taggingMessage.isBlured && !unBlurredMessage" class="blurred" />
              <span :style="{background: highlightBackgroundColor}" class="background" />
              <span
                ref="menuIcon"
                :class="{ underline: highlightBackgroundColor, 'nocopy' : taggingMessage.isBlured && !unBlurredMessage }"
                :data-color="highlightBackgroundColor"
                :style="{textDecorationColor: highlightBackgroundColor}"
              >{{ unBlurredMessage || taggingMessage.message }}</span>
            </div>
            <ContextMenu
              v-click-outside="closeHighlight"
              :item="taggingMessage"
              :options="messagesMenu"
              @onSelected="onMessageMenuSelection"
            />
            <div
              v-show="highlightColorsEnabled"
              ref="contextMenu"
              class="tools colors"
              :style="positionStyles"
            >
              <button
                v-for="(color, index) in colors"
                :key="index"
                :class="{white: color.preview === '#fff'}"
                :style="{backgroundColor: color.preview}"
                class="color"
                @click="setHighlightBackground(color.origin, taggingMessage.id)"
              />
              <button class="close" @click="deleteHighlightBackground(taggingMessage.id)">
                <GreyCloseIcon20 />
              </button>
            </div>
          </div>
          <div
            v-if="taggingMessage.isBlured && hasPermission(['view_origin_content'])"
            class="view-message"
            @click="showMessage()"
          >
            <BlueOpenedEyeIcon24 />
            <span>{{ unBlurredMessage ? 'Hide message' : 'View message' }}</span>
          </div>
          <div v-if="taggingMessage.isPinned" class="pinned">
            <BluePinIcon16 />
            <span>Prioritized</span>
          </div>
        </div>
      </transition>
    </div>
    <div v-if="hasPermission(['add_tag'])" class="menu-item-wrapper">
      <div class="menu-item-header">
        <div class="header-text">
          <h2 class="menu-item-title">
            Tags
          </h2>
          <span @click="$refs.editTagsPopup.openPopups()">Edit</span>
        </div>
        <GreyArrowUpIcon24
          :style="{ transform: isViewTags ? '' : 'rotate(180deg)' }"
          @click="isViewTags = !isViewTags"
        />
      </div>
      <transition v-if="isViewTags" name="slide">
        <div class="tags-wrapper" :class="{ 'tags-disabled': isLoading }">
          <div v-for="(category, index) in messageTagsList" :key="index" class="tag-section">
            <h2 class="section-header">
              {{ category.category.name }}
            </h2>
            <div class="section-tags">
              <div v-for="(tag, i) in category.tags" :key="i" class="tag">
                <div class="check">
                  <CustomCheckbox
                    :id="'tag_' + tag.id"
                    :input-element="tag"
                    custom-class="no-pos"
                    @checked="changeTagState(tag)"
                  />
                </div>
                <span>{{ tag.tag }}</span>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <div class="menu-item-wrapper">
      <div class="menu-item-header">
        <h2 class="menu-item-title">
          Comments
        </h2>
        <GreyArrowUpIcon24
          :style="{ transform: isViewComments ? '' : 'rotate(180deg)' }"
          @click="isViewComments = !isViewComments"
        />
      </div>
      <transition v-if="isViewComments" name="slide">
        <div class="comments-wrapper">
          <div class="comments-item-wrapper">
            <div v-for="(comment, index) in comments" :key="index" class="comment-item">
              <TaggingComment
                :comment="comment"
                @changeComment="handleChangeComment"
                @deleteComment="handleDeleteComment"
              />
            </div>
          </div>
          <textarea
            v-if="hasPermission(['add_comments_highlights'])"
            v-model="newCommentMessage"
            name="invite"
            class="comment-textarea"
            placeholder="Add comment"
            @keydown.enter.exact.prevent="addComment"
          />
          <button
            v-if="hasPermission(['add_comments_highlights'])"
            id="button_create_case"
            class="btn btn-revert"
            @click="addComment"
          >
            Send
          </button>
        </div>
      </transition>
      <EditTagsPopup
        ref="editTagsPopup"
        :tags-list="tagsList"
        @addTag="addTag"
        @deleteTag="deleteTag"
        @changeTag="changeTagName"
        @addNewCategory="addCategory"
        @deleteCategory="deleteCategory"
        @changeTagOrder="tagOrderChange"
        @changeCategory="changeCategoryName"
      />
    </div>
    <ReminderPopup ref="reminderPopup" @reminderAction="reminderAction" />
    <CommonDialog ref="commentDeleteDialogMD">
      <BlueSuccessIcon94 />
      <h2 class="dialog-message-title">
        Success!
      </h2>
      <p class="dialog-message-description" style="display:block;">
        The comment was deleted
      </p>
    </CommonDialog>
    <CommonDialog ref="commonDeleteTagDialog">
      <BlueSuccessIcon94 />
      <h2 class="dialog-message-title">
        Success!
      </h2>
      <p class="dialog-message-description" style="display:block;">
        The tag was deleted.
      </p>
    </CommonDialog>
    <CommonDialog ref="commonDeleteCategoryDialog">
      <BlueSuccessIcon94 />
      <h2 class="dialog-message-title">
        Success!
      </h2>
      <p class="dialog-message-description" style="display:block;">
        The category was deleted.
      </p>
    </CommonDialog>
    <CommonDialog ref="commonDeleteCategoryWarningDialog">
      <h2 class="dialog-message-title">
        Deleting category
      </h2>
      <p class="dialog-message-description" style="display:block;">
        Before you can delete a category, you need to remove
        all tags from it
      </p>
    </CommonDialog>
    <CommonDialog ref="commonDeleteTagWarningDialog">
      <h2 class="dialog-message-title">
        Deleting tag
      </h2>
      <p class="dialog-message-description" style="display:block;">
        Before you can delete this tag, you need to remove it
        from all messages in this chat where it is used
      </p>
    </CommonDialog>
  </div>
</template>

<script>
import CustomCheckbox from '@/components/inputs/CustomCheckbox.vue';
import parseDate from '@/mixins/parseDate';
import parseDateTime from '@/mixins/parseTime';
import AlertErrorMsg from '@/components/profile/AlertErrorMsg.vue';
import ContextMenu from '@/components/popups/ContextMenu.vue';
import highlightImg from '@/assets/img/case-messages/highlight.png';
import redactIcon from '@/assets/img/case-messages/redacted.png';
import priorityIcon from '@/assets/img/case-messages/priority.png';
import reminderImg from '@/assets/img/case-messages/reminder.png';
import deleteIcon from '@/assets/img/case-messages/delete.svg';
import unredactIcon from '@/assets/img/case-messages/unredact.svg';
import editIcon from '@/assets/img/case-messages/edit.svg';
import EditTagsPopup from '@/components/popups/EditTagsPopup.vue';
import ReminderPopup from '@/components/popups/ReminderPopup.vue';
import dayjs from 'dayjs';
import TaggingComment from '@/components/cases/case-messages/tagging-common/TaggingComment.vue';
import CommonDialog from '@/components/popups/CommonDialog.vue';
import hasPermission from '@/utils/permission';
import messagesTagging from '@/mixins/messagesTagging';
import {mapGetters} from 'vuex';
import WhiteCloseIcon28 from '@/components/icons/WhiteCloseIcon28.vue';
import GreyArrowUpIcon24 from '@/components/icons/GreyArrowUpIcon24.vue';
import GreyCloseIcon20 from '@/components/icons/GreyCloseIcon20.vue';
import BlueOpenedEyeIcon24 from '@/components/icons/BlueOpenedEyeIcon24.vue';
import BluePinIcon16 from '@/components/icons/BluePinIcon16.vue';
import BlueSuccessIcon94 from '@/components/icons/BlueSuccessIcon94.vue';

const timeOptions = [
  {id: 1, name: 'In 20 minutes'},
  {id: 2, name: 'In 1 hour'},
  {id: 3, name: 'In 3 hours'},
  {id: 4, name: 'Tomorrow'},
  {id: 5, name: 'Next week'},
  {id: 6, name: 'Custom...'}
];

const messageOptions = [
  {id: 1, name: 'Highlight', img: highlightImg},
  {id: 2, name: 'Redact text', img: redactIcon},
  {id: 3, name: 'Mark priority', img: priorityIcon},
  {id: 4, name: 'Remind me about this', img: reminderImg, children: timeOptions},
];

const commentOptions = [
  {name: 'Edit', img: editIcon},
  {name: 'Delete', img: deleteIcon},
];

export default {
  name: 'MessagesTaggingPanel',
  components: {
    BlueSuccessIcon94,
    BluePinIcon16,
    BlueOpenedEyeIcon24,
    GreyCloseIcon20,
    GreyArrowUpIcon24,
    WhiteCloseIcon28,
    CommonDialog,
    TaggingComment,
    ReminderPopup,
    EditTagsPopup,
    ContextMenu,
    AlertErrorMsg,
    CustomCheckbox,
  },
  emits: ['updateMessages', 'closeDetails', 'changeCheckbox'],
  mixins: [parseDate, parseDateTime, messagesTagging],
  props: {
    updateMessageById: {
      type: Number,
      default: null,
    },
    updateCurrentMessage: {
      type: Number,
      default: null,
    }
  },
  data() {
    return {
      unredactIcon,
      highlightImg,
      redactedImg: redactIcon,
      priorityImg: priorityIcon,
      reminderImg,
      deleteIcon,
      timeOptions: timeOptions,
      commentOptions: commentOptions,
      messageOptions: messageOptions,
      newComment: '',
      errorMessage: '',
      newCommentMessage: '',
      infoDialogData: {
        dialogTitle: '',
        dialogText: '',
        confirmBtnText: '',
        cancelBtnText: '',
      },
      commentEdit: false,
      isViewMessage: true,
      isViewTags: true,
      isViewComments: true,
      highlightBackgroundColor: null,
      highlightColorsEnabled: false,
      colors: [
        {origin: 'rgba(255,236,67,0.4)', preview: 'rgba(255,236,67)'},
        {origin: 'rgba(155,126,222,0.4)', preview: 'rgba(155,126,222)'},
        {origin: 'rgba(4,83,202,0.4)', preview: 'rgba(4,83,202)'},
        {origin: 'rgba(255,149,55,0.4)', preview: 'rgba(255,149,55)'},
        {origin: 'rgba(241,16,95,0.4)', preview: 'rgba(241,16,95)'},
        {origin: 'rgba(60,193,59,0.4)', preview: 'rgba(60,193,59)'}
      ],
      positionStyles: {
        position: 'absolute',
        top: '0px',
        left: '0px',
      },
    };
  },
  computed: {
    ...mapGetters('messagesTagging', {
      isLoading: 'getLoadingState',
    }),
    messagesMenu() {
      let options = this.messageOptions;

      if (this.taggingMessage.isPinned) {
        options
          .filter(option => option.name === 'Mark priority')
          .map(option => {
            option.name = 'Remove from Priorities';
            option.img = deleteIcon;
          });
      } else {
        options
          .filter(option => option.name === 'Remove from Priorities')
          .map(option => {
            option.name = 'Mark priority';
            option.img = priorityIcon;
          });
      }


      if (this.taggingMessage.isBlured) {
        options
          .filter(option => option.name === 'Redact text')
          .map(option => {
            option.name = 'Unredact text';
            option.img = unredactIcon;
          });
      } else if (this.taggingMessage.message.length > 0) {
        options
          .filter(option => option.name === 'Unredact text')
          .map(option => {
            option.name = 'Redact text';
            option.img = redactIcon;
          });
      } else {
        options = options.filter(option => option.name !== 'Redact text' && option.name !== 'Unredact text');
      }
      return options;
    }
  },
  watch: {
    'chatData.taggingMessageId': function (newVal) {
      if (newVal && this.isTagging) {
        this.getMessageById(newVal);
      }
    },
    async updateCurrentMessage(newVal) {
      if (newVal) {
        await this.getMessageById(newVal);
      }
    },
    async updateMessageById(newVal) {
      if (newVal && newVal !== this.taggingMessage.id) {
        await this.getMessageById(newVal);
      }
    },
    taggingMessage: {
      handler() {
        this.highlightBackgroundColor = this.taggingMessage.highlight ? this.taggingMessage.highlight : null;
      },
      deep: true,
    }
  },
  async mounted() {
    await this.getMessageById();
    await this.getListTags();
    this.debounce(() => {
      this.highlightBackgroundColor = this.taggingMessage.highlight ? this.taggingMessage.highlight : null;
    }, 1000);
  },
  methods: {
    hasPermission,
    positionMenu(triggerElement) {
      const contextMenu = this.$refs.contextMenu;
      let iconRect;

      if (triggerElement) {
        iconRect = triggerElement.getBoundingClientRect();
      } else {
        const menuIcon = this.$refs.menuIcon;
        iconRect = menuIcon.getBoundingClientRect();
      }

      const menuRect = contextMenu.getBoundingClientRect();
      let top, left;

      top = iconRect.bottom;
      left = iconRect.left;

      // If menu goes off bottom, position above
      if (top + menuRect.height > window.innerHeight) {
        top = iconRect.top - menuRect.height;
      }

      // If menu goes off right edge, adjust left
      if (left + menuRect.width > window.innerWidth) {
        left = window.innerWidth - menuRect.width;
      }

      top = Math.max(top, 0);
      left = Math.max(left, 0);

      this.positionStyles.top = `${top}px`;
      this.positionStyles.left = `${left}px`;
      this.positionStyles.position = 'fixed';
    },

    openHighlight(event) {
      this.highlightColorsEnabled = !this.highlightColorsEnabled;
      this.positionMenu(event.currentTarget);
    },
    closeHighlight() {
      this.highlightColorsEnabled = false;
    },
    async setHighlightBackground(color) {
      await this.createHighlightForMessage(color);
      this.highlightBackgroundColor = color;
      this.closeHighlight();
      this.$emit('updateMessages', this.taggingMessage);
    },
    deleteHighlightBackground() {
      this.deleteHighlightForMessage();
      this.highlightBackgroundColor = null;
      this.closeHighlight();
      this.$emit('updateMessages', this.taggingMessage);
    },

    addComment() {
      this.$load(async () => {
        if (this.newCommentMessage) {
          await this.createCommentForMessage(this.newCommentMessage);
          this.newCommentMessage = '';
          this.$emit('updateMessages', this.taggingMessage);
        }
      }, (error) => {
        this.handleResponseError(error);
      });
    },
    openDeleteCommentSuccessPopup() {
      this.$refs.commentDeleteDialogMD.openPopups();
    },

    onMessageMenuSelection(optionName, event) {
      if (messageOptions.some(option => option.name === optionName)) {
        this.handleMessageMenuSelection(optionName, event);
      } else if (timeOptions.some(option => option.name === optionName)) {
        this.handleTimeOptionSelection(optionName);
      } else {
        this.errorMessage = 'No such option';
      }
    },
    handleMessageMenuSelection(optionName, event) {
      switch (optionName) {
        case 'Highlight':
          this.openHighlight(event);
          break;
        case 'Mark priority':
        case 'Remove from Priorities':
          this.markPriority();
          break;
        case 'Redact text':
        case 'Unredact text':
          this.redactMessage();
          break;
        default:
          this.errorMessage = 'No such option';
      }
    },
    handleTimeOptionSelection(optionName) {
      // Compute the date and time based on the selected time option
      const now = dayjs();
      let targetDateTime;

      switch (optionName) {
        case 'In 20 minutes':
          targetDateTime = now.add(20, 'minute');
          break;
        case 'In 1 hour':
          targetDateTime = now.add(1, 'hour');
          break;
        case 'In 3 hours':
          targetDateTime = now.add(3, 'hour');
          break;
        case 'Tomorrow':
          targetDateTime = now.add(1, 'day').startOf('day').add(9, 'hour');
          break;
        case 'Next week':
          targetDateTime = now.add(1, 'week').startOf('day').add(9, 'hour');
          break;
        case 'Custom...':
          this.$refs.reminderPopup.openPopups();
          break;
        default:
          this.errorMessage = 'No such option';
      }

      if (targetDateTime) {
        const utcDateTime = targetDateTime.utc();
        this.addReminder(utcDateTime.format('YYYY-MM-DDTHH:mm:ss'));
      }
    },

    reminderAction(reminderInfo) {
      const targetDateTime = dayjs(reminderInfo.reminderTime);
      const utcDateTime = targetDateTime.utc();
      reminderInfo.reminderTime = utcDateTime.format('YYYY-MM-DDTHH:mm:ss');
      if (!reminderInfo.reminderId) {
        this.addReminder(reminderInfo.reminderTime);
      } else {
        this.editReminder(reminderInfo);
      }
    },

    markPriority() {
      if (this.taggingMessage.isPinned) {
        this.unpinMessage();
      } else {
        this.pinMessage();
      }
      this.$emit('updateMessages', this.taggingMessage);
    },

    async redactMessage() {
      if (this.taggingMessage.isBlured) {
        await this.unblurMessage();
      } else {
        await this.blurMessage();
      }
      this.$emit('updateMessages', this.taggingMessage);
    },
    showMessage() {
      if (this.taggingMessage.isBlured && this.unBlurredMessage) {
        this.clearUnBlurredMessage();
      } else {
        this.showBlurred();
      }
    },

    async changeTagState(tag) {
      if (tag.isActive) {
        await this.addTagToMessage(tag.id);
      } else {
        await this.removeTagFromMessage(tag.id);
      }
      this.$emit('updateMessages', this.taggingMessage);
    },

    closeDetails() {
      this.$router.push({name: 'Case messages mobile chats'});
    },

    // COMMENTS
    handleChangeComment(commentId, newText) {
      this.changeComment(commentId, newText);
      this.$emit('updateMessages', this.taggingMessage);
    },
    async handleDeleteComment(commentId) {
      await this.deleteComment(commentId);
      this.openDeleteCommentSuccessPopup();
      this.$emit('updateMessages', this.taggingMessage);
    },

    // EDIT TAGS POPUP
    // CATEGORIES
    addCategory(categoryName) {
      this.addNewCategory(categoryName);
    },
    changeCategoryName(categoryInfo) {
      this.editCategory(categoryInfo);
    },
    deleteCategory(categoryId) {
      this.$load(async () => {
        await this.removeCategory(categoryId);
        this.$refs.commonDeleteCategoryDialog.openPopups();
      }, () => {
        this.$refs.commonDeleteCategoryWarningDialog.openPopups();
      });
    },

    // TAGS
    addTag(tagInfo) {
      this.addNewTag(tagInfo);
    },
    deleteTag(tagId) {
      this.$load(async () => {
        await this.removeTag(tagId);
        this.$refs.commonDeleteTagDialog.openPopups();
      }, () => {
        this.$refs.commonDeleteTagWarningDialog.openPopups();
      });
    },
    changeTagName(tagInfo) {
      this.editTag(tagInfo);
    },
    tagOrderChange(item) {
      this.changeTagOrder(item);
    },
  },
};
</script>
<style scoped lang="scss">
.message-details-wrapper {
  max-width: 435px;
  max-height: 1031px;
  width: 100%;
  height: 85vh;
  border-left: 1px solid var(--border-color);
  scrollbar-color: var(--background-color) transparent;
  scrollbar-width: thin;

  .message-details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 25px;

    .message-details-title {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      line-height: 21.78px;
      color: var(--secondary-black-color);
    }
  }

  .menu-item-wrapper {

    .menu-item-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 25px;
      background-color: var(--hover-accent-color);

      .header-text {
        display: flex;
        align-items: center;
        gap: 10px;

        span {
          font-size: 14px;
          font-weight: 400;
          line-height: 16.94px;
          text-align: left;
          color: var(--light-gray);
          cursor: pointer;
        }
      }

      .menu-item-title {
        font-size: 14px;
        font-weight: 700;
        line-height: 21px;
        text-align: left;
        text-transform: uppercase;
        color: var(--secondary-black-color);
      }

      svg {
        transition: transform 0.3s;
      }
    }
  }
}

.message-text {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
  padding: 15px 25px 25px;
  transition: all .5s;

  .message-wrapper {
    display: flex;
    justify-content: space-between;
    max-height: 200px;
    height: fit-content;
    overflow-y: scroll;

    .relative {
      position: relative;
      width: 100%;

      span {
        font-size: 14px;
        font-weight: 500;
        line-height: 19.2px;
        text-align: left;
        color: var(--black-color);
        margin: 0;
      }
    }
  }

  .pinned,
  .view-message {
    display: flex;
    align-items: center;
    gap: 5px;

    span {
      font-size: 14px;
      font-weight: 400;
      line-height: 16px;
      text-align: left;
      color: var(--bright-accent-color)
    }
  }

  .view-message {
    cursor: pointer;
  }
}

.tags-wrapper {
  padding: 0 25px 15px;
  transition: all .5s;
  height: 190px;
  overflow-y: scroll;

  .tag-section {
    padding-top: 15px;

    .section-header {
      font-size: 16px;
      font-weight: 700;
      line-height: 19.36px;
      text-align: left;
      color: var(--accent-color);
      padding-bottom: 15px;
    }

    .section-tags {

      .tag {
        display: flex;
        gap: 10px;
      }
    }

    &:not(:last-child) {
      padding-bottom: 15px;
      border-bottom: 1px solid var(--border-color);
    }
  }
}

.tags-disabled {
  opacity: 0.1;
  pointer-events: none;
}

.comments-wrapper {
  padding: 0 0 25px 25px;

  .comments-item-wrapper {
    height: fit-content;
    max-height: 190px;
    overflow-y: scroll;
  }

  .comment-textarea {
    width: 100%;
    resize: none;
    min-height: 104px;
    outline: none;
    border: 1px solid var(--input-placeholder-value-color);
    border-radius: 16px !important;
    padding: 16px 24px;
    font-size: 14px;
    font-weight: 500;
    background: transparent;
    z-index: 2;
    color: var(--secondary-text-color);
    margin: 15px 0;
  }

  .btn {
    max-width: 120px;
    height: 35px;
  }
}

h2 {
  margin: 0;
}

svg {
  cursor: pointer;
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter, .slide-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.slide-enter-to, .slide-leave {
  max-height: 1000px;
  opacity: 1;
}

.tools {
  position: absolute;
  z-index: 10;
  transition: 0.2s all;
  border: 1px solid var(--case-btn-border-color);
  border-radius: 8px;
  padding: 8px;
  background-color: var(--white-color);
  font-size: 12px;

  max-width: fit-content;
  min-width: 250px;
  width: 100%;
  height: fit-content;
  box-shadow: 0 4px 8px 0 #00000029;
}

.colors {
  display: flex;
  align-items: center;
  gap: 16px;

  .close {
    border: none;
    width: fit-content;
    padding: 0;
    background-color: transparent;
  }

  .color {
    border: 1px solid transparent;
    border-radius: 50%;
    width: 16px;
    height: 16px;

    &.white {
      border-color: var(--case-btn-border-color);
    }

    &:hover,
    &.active {
      border-color: #373c44;
    }
  }
}

.underline {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.background {
  position: absolute;
  z-index: -1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.blurred {
  position: absolute;
  z-index: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: var(--black-color);
  pointer-events: none;
  width: 100%;
}

.edit-message-textarea {
  display: block;
  border: .5px solid var(--light-gray);
  border-radius: 0 !important;
  height: auto;
  padding: 0 !important;
  width: 100%;
}

.dialog-message-title {
  font-style: normal;
  font-weight: bold;
  font-size: 30px;
  line-height: 36px;
  color: #292B2F;
  text-align: center;
  margin: 0;
  display: block;
  width: 100%;
  margin-top: 25px;
}

.dialog-message-description {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 26px;
  text-align: center;
  color: #696F79;
  width: 100%;
  margin-top: 20px;
}

.dialog-message-title {
  font-style: normal;
  font-weight: bold;
  font-size: 30px;
  line-height: 36px;
  color: #292B2F;
  text-align: center;
  margin: 0;
  display: block;
  width: 100%;
  margin-top: 25px;
}

.dialog-message-description {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 26px;
  text-align: center;
  color: #696F79;
  width: 100%;
  max-width: 400px;
  margin-top: 20px;
}

.no-pos {
  position: unset;
}

</style>
